import React, { useState, useEffect, useCallback } from 'react';
import Navbar from '../components/Navbar';
import CalendarHeader from '../components/calendar/CalendarHeader';
import WeekGrid from '../components/calendar/WeekGrid';
import NewPostModal from '../components/calendar/NewPostModal';
import { indianHolidays2026 } from '../constants/indianHolidays2026';
import { API_BASE_URL, API_ENDPOINTS } from '../constants/apiConstants';
import { getStoredTokens, getStoredUserId } from '../utils/auth';

// ─── helpers ────────────────────────────────────────────────────────────────

const buildBffUrl = (path) => {
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  const baseUrl = API_BASE_URL?.replace(/\/$/, '') || '';
  if (baseUrl.endsWith('/api/bff') && normalizedPath.startsWith('/api/bff/')) {
    return `${baseUrl}${normalizedPath.replace('/api/bff', '')}`;
  }
  return `${baseUrl}${normalizedPath}`;
};

const getRequestUserId = () => {
  const storedUserId = getStoredUserId();
  if (storedUserId) return storedUserId;
  const tokens = getStoredTokens();
  if (tokens?.idToken) {
    try {
      const parts = tokens.idToken.split('.');
      if (parts.length === 3) {
        const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));
        if (payload?.sub) return payload.sub;
      }
    } catch (_) {}
  }
  return null;
};

/** Map a scheduled_posts DB row → calendar post shape */
const mapScheduledPost = (row) => ({
  id: row.id,
  title: row.caption || 'Untitled post',
  platform: row.platform
    ? row.platform.charAt(0).toUpperCase() + row.platform.slice(1)
    : 'LinkedIn',
  scheduledDate: new Date(row.scheduled_at),
  time: new Date(row.scheduled_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
  status: row.status === 'published' ? 'published'
    : row.status === 'failed' ? 'failed'
    : row.status === 'cancelled' ? 'cancelled'
    : 'scheduled',
  // extra fields for editing
  image_url: row.image_url,
  organization_id: row.organization_id,
  organization_name: row.organization_name,
  ai_scheduled: row.ai_scheduled,
  post_url: row.post_url,
  error_message: row.error_message,
  _raw: row
});

// ─── component ───────────────────────────────────────────────────────────────

const Calendar = () => {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [viewMode, setViewMode] = useState('calendar');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedPost, setSelectedPost] = useState(null);
  const [filters, setFilters] = useState({
    channel: 'all',
    status: 'all',
    tags: 'all',
    timezone: 'Asia/Kolkata',
    showHolidays: true
  });

  const [posts, setPosts] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [fetchError, setFetchError] = useState('');

  // ── fetch scheduled posts from backend ──────────────────────────────────
  const fetchScheduledPosts = useCallback(async () => {
    const tokens = getStoredTokens();
    const userid = getRequestUserId();
    if (!tokens?.idToken || !userid) return;

    setIsLoading(true);
    setFetchError('');
    try {
      const res = await fetch(buildBffUrl(API_ENDPOINTS.SCHEDULED_POSTS), {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${tokens.idToken}`,
          'userid': userid
        }
      });
      const data = await res.json();
      if (data.success && Array.isArray(data.data)) {
        setPosts(data.data.map(mapScheduledPost));
      } else {
        setFetchError(data.message || 'Failed to load scheduled posts');
      }
    } catch (err) {
      console.error('Failed to fetch scheduled posts:', err);
      setFetchError('Could not connect to the server');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchScheduledPosts();
    window.addEventListener('adflyn:posts:updated', fetchScheduledPosts);
    return () => window.removeEventListener('adflyn:posts:updated', fetchScheduledPosts);
  }, [fetchScheduledPosts]);

  // ── week navigation ──────────────────────────────────────────────────────
  const getWeekDates = (date) => {
    const current = new Date(date);
    const first = current.getDate() - current.getDay();
    return Array.from({ length: 7 }, (_, i) => {
      const day = new Date(current);
      day.setDate(first + i);
      return day;
    });
  };

  const weekDates = getWeekDates(currentMonth);

  const filteredPosts = posts.filter(post => {
    if (filters.channel !== 'all' && post.platform.toLowerCase() !== filters.channel.toLowerCase()) return false;
    if (filters.status !== 'all' && post.status !== filters.status) return false;
    return true;
  });

  const aiScheduledPosts = posts.filter(p => p.ai_scheduled);

  const handlePrevMonth = () => {
    const d = new Date(currentMonth);
    d.setDate(d.getDate() - 7);
    setCurrentMonth(d);
  };

  const handleNextMonth = () => {
    const d = new Date(currentMonth);
    d.setDate(d.getDate() + 7);
    setCurrentMonth(d);
  };

  const handleToday = () => setCurrentMonth(new Date());

  const handleNewPost = () => {
    setSelectedPost(null);
    setIsModalOpen(true);
  };

  const handlePostClick = (post) => {
    console.log('Post clicked:', post);
  };

  const handlePostEdit = (post) => {
    setSelectedPost(post);
    setIsModalOpen(true);
  };

  const handlePostDuplicate = async (post) => {
    const tokens = getStoredTokens();
    const userid = getRequestUserId();
    if (!tokens?.idToken || !userid) return;

    // Schedule a copy for tomorrow at the same time
    const newDate = new Date(post.scheduledDate);
    newDate.setDate(newDate.getDate() + 1);

    try {
      const res = await fetch(buildBffUrl(API_ENDPOINTS.SCHEDULED_POSTS), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${tokens.idToken}`,
          'Content-Type': 'application/json',
          'userid': userid
        },
        body: JSON.stringify({
          caption: post.title,
          image_url: post.image_url,
          platform: post.platform.toLowerCase(),
          organization_id: post.organization_id,
          organization_name: post.organization_name,
          scheduled_at: newDate.toISOString()
        })
      });
      const data = await res.json();
      if (data.success) {
        await fetchScheduledPosts();
      }
    } catch (err) {
      console.error('Failed to duplicate post:', err);
    }
  };

  const handlePostDelete = async (post) => {
    // eslint-disable-next-line no-restricted-globals
    if (!confirm('Are you sure you want to cancel this scheduled post?')) return;

    const tokens = getStoredTokens();
    const userid = getRequestUserId();
    if (!tokens?.idToken || !userid) return;

    try {
      const res = await fetch(buildBffUrl(`${API_ENDPOINTS.SCHEDULED_POSTS}/${post.id}`), {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${tokens.idToken}`,
          'userid': userid
        }
      });
      const data = await res.json();
      if (data.success) {
        setPosts(prev => prev.filter(p => p.id !== post.id));
      }
    } catch (err) {
      console.error('Failed to delete post:', err);
    }
  };

  const handleSavePost = async (post) => {
    const tokens = getStoredTokens();
    const userid = getRequestUserId();
    if (!tokens?.idToken || !userid) return;

    const scheduledAt = new Date(`${post.scheduledDate instanceof Date
      ? post.scheduledDate.toISOString().split('T')[0]
      : post.scheduledDate}T${post.time || '10:00'}`);

    try {
      if (selectedPost?.id) {
        // Update existing
        const res = await fetch(buildBffUrl(`${API_ENDPOINTS.SCHEDULED_POSTS}/${selectedPost.id}`), {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${tokens.idToken}`,
            'Content-Type': 'application/json',
            'userid': userid
          },
          body: JSON.stringify({
            caption: post.title,
            platform: (post.platform || 'linkedin').toLowerCase(),
            scheduled_at: scheduledAt.toISOString()
          })
        });
        const data = await res.json();
        if (data.success) {
          await fetchScheduledPosts();
        }
      } else {
        // Create new from calendar modal
        const res = await fetch(buildBffUrl(API_ENDPOINTS.SCHEDULED_POSTS), {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${tokens.idToken}`,
            'Content-Type': 'application/json',
            'userid': userid
          },
          body: JSON.stringify({
            caption: post.title,
            platform: (post.platforms?.[0] || post.platform || 'linkedin').toLowerCase(),
            scheduled_at: scheduledAt.toISOString()
          })
        });
        const data = await res.json();
        if (data.success) {
          await fetchScheduledPosts();
        }
      }
    } catch (err) {
      console.error('Failed to save post:', err);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />

      <div className="max-w-[1600px] mx-auto p-6">
        {/* Page Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Social Media Calendar</h1>
          <p className="text-gray-600">Plan, schedule, and manage your social media content</p>
        </div>

        {/* Loading / error states */}
        {isLoading && (
          <div className="mb-4 flex items-center gap-2 text-indigo-600 text-sm font-medium">
            <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
            </svg>
            Loading scheduled posts…
          </div>
        )}
        {fetchError && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
            {fetchError}
            <button
              className="ml-3 underline text-red-600"
              onClick={fetchScheduledPosts}
            >
              Retry
            </button>
          </div>
        )}

        {/* Calendar Header with Filters */}
        <CalendarHeader
          currentMonth={currentMonth}
          weekDates={weekDates}
          onPrevMonth={handlePrevMonth}
          onNextMonth={handleNextMonth}
          onToday={handleToday}
          viewMode={viewMode}
          setViewMode={setViewMode}
          filters={filters}
          setFilters={setFilters}
          onNewPost={handleNewPost}
        />

        {/* Weekly Calendar Grid */}
        {viewMode === 'calendar' && (
          <WeekGrid
            weekDates={weekDates}
            holidays={filters.showHolidays ? indianHolidays2026 : []}
            posts={filteredPosts}
            onPostClick={handlePostClick}
            onPostEdit={handlePostEdit}
            onPostDuplicate={handlePostDuplicate}
            onPostDelete={handlePostDelete}
          />
        )}

        {/* List View */}
        {viewMode === 'list' && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            {filteredPosts.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                No scheduled posts found. Go to the Gallery to schedule your first post.
              </div>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="text-left px-4 py-3 font-semibold text-gray-700">Caption</th>
                    <th className="text-left px-4 py-3 font-semibold text-gray-700">Platform</th>
                    <th className="text-left px-4 py-3 font-semibold text-gray-700">Scheduled</th>
                    <th className="text-left px-4 py-3 font-semibold text-gray-700">Status</th>
                    <th className="px-4 py-3" />
                  </tr>
                </thead>
                <tbody>
                  {filteredPosts.map(post => (
                    <tr key={post.id} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="px-4 py-3 max-w-xs truncate text-gray-900">{post.title}</td>
                      <td className="px-4 py-3 text-gray-600">{post.platform}</td>
                      <td className="px-4 py-3 text-gray-600 whitespace-nowrap">
                        {post.scheduledDate.toLocaleDateString()} {post.time}
                        {post.ai_scheduled && (
                          <span className="ml-2 text-xs text-indigo-500 font-medium">✨ AI</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                          post.status === 'published' ? 'bg-green-100 text-green-700'
                          : post.status === 'failed' ? 'bg-red-100 text-red-700'
                          : post.status === 'cancelled' ? 'bg-gray-100 text-gray-500'
                          : 'bg-indigo-100 text-indigo-700'
                        }`}>
                          {post.status}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right whitespace-nowrap">
                        {post.status === 'scheduled' && (
                          <>
                            <button
                              onClick={() => handlePostEdit(post)}
                              className="text-indigo-600 hover:underline mr-3 text-xs font-medium"
                            >
                              Edit
                            </button>
                            <button
                              onClick={() => handlePostDelete(post)}
                              className="text-red-500 hover:underline text-xs font-medium"
                            >
                              Cancel
                            </button>
                          </>
                        )}
                        {post.post_url && (
                          <a
                            href={post.post_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:underline text-xs font-medium ml-3"
                          >
                            View post ↗
                          </a>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        {/* AI Schedules View */}
        {viewMode === 'ai_schedules' && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-100 flex items-center gap-2">
              <span className="text-lg">✨</span>
              <h2 className="text-base font-semibold text-gray-900">AI Scheduled Posts</h2>
              <span className="ml-auto text-sm text-gray-500">{aiScheduledPosts.length} posts</span>
            </div>
            {aiScheduledPosts.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                No AI-scheduled posts yet. Go to the Chatbot and ask it to plan
                and schedule a LinkedIn campaign.
              </div>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="text-left px-4 py-3 font-semibold text-gray-700">Caption</th>
                    <th className="text-left px-4 py-3 font-semibold text-gray-700">Platform</th>
                    <th className="text-left px-4 py-3 font-semibold text-gray-700">Scheduled</th>
                    <th className="text-left px-4 py-3 font-semibold text-gray-700">Status</th>
                    <th className="px-4 py-3" />
                  </tr>
                </thead>
                <tbody>
                  {aiScheduledPosts.map(post => (
                    <tr key={post.id} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="px-4 py-3 max-w-xs truncate text-gray-900">{post.title}</td>
                      <td className="px-4 py-3 text-gray-600">{post.platform}</td>
                      <td className="px-4 py-3 text-gray-600 whitespace-nowrap">
                        {post.scheduledDate.toLocaleDateString()} {post.time}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                          post.status === 'published' ? 'bg-green-100 text-green-700'
                          : post.status === 'failed' ? 'bg-red-100 text-red-700'
                          : 'bg-indigo-100 text-indigo-700'
                        }`}>{post.status}</span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        {post.post_url && (
                          <a href={post.post_url} target="_blank" rel="noopener noreferrer"
                            className="text-blue-600 hover:underline text-xs font-medium">
                            View post ↗
                          </a>
                        )}
                        {post.status === 'scheduled' && (
                          <button onClick={() => handlePostDelete(post)}
                            className="text-red-500 hover:underline text-xs font-medium ml-3">
                            Cancel
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        {/* New / Edit Post Modal */}
        <NewPostModal
          isOpen={isModalOpen}
          onClose={() => {
            setIsModalOpen(false);
            setSelectedPost(null);
          }}
          onSave={handleSavePost}
          editingPost={selectedPost}
        />
      </div>
    </div>
  );
};

export default Calendar;
